import CloseIcon from "@mui/icons-material/Close";
import { Box, IconButton, Tooltip, useTheme } from "@mui/material";
import type { GridColumnVisibilityModel } from "@mui/x-data-grid";
import { type ReactNode, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { InHouseTooltip } from "../../../components/InHouseTooltip/InHouseTooltip";
import { measureTextWidth } from "../../../components/UIToolkit/Inputs/SystemDropDown/fitNames";
import { MetricThresholdBadge } from "../../../components/UIToolkit/Tables/MetricThresholdBadge/MetricThresholdBadge";
import {
	type MetricStatus,
	type MetricThresholds,
	OVER_REFERENCE_THRESHOLDS,
	PERCENT_HIGHER_IS_BETTER_THRESHOLDS,
	resolveStatusAgainstReference,
} from "../../../components/UIToolkit/Tables/MetricThresholdBadge/metricThresholds";
import type { SyntheticPickerColumn, SystemGridColDef } from "../../../components/UIToolkit/Tables/SystemDataGrid";
import type { TranslationKeys } from "../../../i18n/types";
import { INK } from "../../../theme/tokens";
import type { PushReportColumn } from "../../../utils/models";
import type { MultiSortOption } from "../../../utils/multiColumnSorting";
import { toKebabCase } from "../../../utils/strings";
import { METRIC_GROUP_LABEL_KEYS, type MetricColumnGroupKey, metricGroupForField } from "../../OpsBackoffice/metricColumnGroups";

/**
 * Identity + metric columns the multi-facility grid renders — the narrow contract of the
 * network-scoped `/multi/backoffice-grid` endpoint. Deliberately a subset of the ops-backoffice
 * dashboard row: the ops-internal fields (notes, ops owner, stage, onboarding dates) stay on the
 * support-gated route and never reach this head-nurse view.
 *
 * Every money-derived field is nullable because the backend blanks it (rather than omitting it) for
 * a hide-wage caller — see `MultiFacilityGridRow.from_dashboard_row`.
 */
export interface MultiFacilityGridRow {
	departmentId: string;
	departmentName: string;
	avgActualHppd: number | null;
	targetHppd: number;
	hppdDiffFromTarget: number | null;
	avgScheduledHppd: number | null;
	// HPPD by role and by agency split (off by default; revealed via their picker group rows).
	avgActualHppdCna: number | null;
	avgActualHppdLpn: number | null;
	avgActualHppdRn: number | null;
	avgActualHppdAgency: number | null;
	avgActualHppdNonAgency: number | null;
	avgActualPpd: number | null;
	targetPpd: number | null;
	ppdDiffFromTarget: number | null;
	avgScheduledPpd: number | null;
	// PPD by role and by agency split (off by default; revealed via their picker group rows).
	avgActualPpdCna: number | null;
	avgActualPpdLpn: number | null;
	avgActualPpdRn: number | null;
	avgActualPpdAgency: number | null;
	avgActualPpdNonAgency: number | null;
	otCostPercent: number | null;
	otHoursPercent: number | null;
	agencyCostPercent: number | null;
	actualAgencyHours: number;
	// Per-role OT breakdown (off by default; revealed via the two OT-breakdown picker groups).
	actualOtHoursCna: number;
	actualOtHoursLpn: number;
	actualOtHoursRn: number;
	otHoursPercentCna: number | null;
	otHoursPercentLpn: number | null;
	otHoursPercentRn: number | null;
	loggedInPercent: number;
	wauPercent: number;
	wauCount: number;
	schedulerLoginCount: number;
	schedulerActionsCount: number;
	adminLoginCount: number;
	shiftPickupPercent: number;
	filledPercent: number;
}

export interface MultiFacilityGridResponse {
	departments: MultiFacilityGridRow[];
	/** Facilities whose money columns were zeroed because this caller may not see their wage data. */
	wageRestrictedDepartmentIds: string[];
}

/**
 * Field id of the row-level remove control. Synthetic (no such key on the row), so it is spelled out
 * once here and kept out of `MultiFacilityGridRow` — tests and the column set reference this rather
 * than a bare string.
 */
export const MULTI_FACILITY_GRID_REMOVE_FIELD = "removeFacility";

const FACILITY_COLUMN_MIN_WIDTH = 200;
// Flex weights fill the grid's width; the facility column is weighted heavier so the (potentially long)
// department name gets more room than a metric cell.
const FACILITY_COLUMN_FLEX = 1.4;
const METRIC_COLUMN_FLEX = 1;
// minWidth floors are the anti-truncation guard: when many columns are shown the grid can't flex-grow
// past the container, so every column sits at its floor — the floor must fit the header label. A center-
// aligned header reserves ~50px of chrome (cell padding + the sort and drag-reorder icons); measured
// in-app, a label wider than ~95px already ran under those icons at the old 140px floor. The two tier
// floors keep the English grid on an even rhythm: the short tier's widest English label is
// "Onboarding (%)" (109px text, so 160px), the wide tier's needs ~185px, inside 205px. A translated label
// or a wider user-chosen font can still outgrow its tier, so each column's floor is raised to its
// measured label plus that chrome when the tier alone would clip it.
const METRIC_COLUMN_MIN_WIDTH = 160;
const WIDE_METRIC_COLUMN_MIN_WIDTH = 205;
const METRIC_HEADER_CHROME_WIDTH = 50;
/** Just the icon button plus cell padding — this column has no header label to fit. */
const REMOVE_COLUMN_WIDTH = 56;

/**
 * Frozen on horizontal scroll so each row stays identifiable while reading metrics far to the right.
 * Both columns are always visible and non-reorderable, so they are always the grid's first two.
 */
export const MULTI_FACILITY_GRID_FROZEN_FIELDS: readonly string[] = [MULTI_FACILITY_GRID_REMOVE_FIELD, "departmentName"];
/** The Facility column is last in the frozen zone, so it can flex; only the columns before it need a fixed width. */
export const MULTI_FACILITY_GRID_FROZEN_WIDTHS: Readonly<Record<string, number>> = { [MULTI_FACILITY_GRID_REMOVE_FIELD]: REMOVE_COLUMN_WIDTH };

/**
 * The metric column "set" for the multi-facility grid, as data. This mirrors the ops-backoffice
 * preset shape — an ordered field list with a default-visibility flag — but lives in code rather than
 * the preset table: this is a fixed set on a head-nurse page, and the preset feature is support-gated
 * and scoped to the ops column universe (see PR discussion). `optional: true` columns are offered in
 * the column picker but start hidden; everything else is visible by default.
 */
type MetricColumnSpec = {
	field: keyof MultiFacilityGridRow;
	headerName: TranslationKeys;
	/**
	 * Plain-language definition shown on header hover. Required on every metric: the simplified column
	 * set exists so clients can be educated on what they're looking at, and a column nobody can define
	 * is exactly the kind that generates more questions than it answers.
	 */
	tooltip: TranslationKeys;
	minWidth: number;
	optional?: boolean;
	/**
	 * Kept out of the picker's own per-column list — its visibility is driven by a synthetic group row
	 * instead (see `PICKER_GROUP_SPECS`). The column still renders when that group toggles it on.
	 */
	excludeFromColumnPicker?: boolean;
	/**
	 * Money-derived column. Dropped from the grid entirely when the portfolio wage gate is closed — the
	 * backend already blanks these fields, so without this the user would see a column of empty cells
	 * and read it as missing data rather than as withheld.
	 */
	wageBearing?: boolean;
	/**
	 * Formats the raw cell value for on-screen display (non-badge cells). Carries the unit ($ / %), so
	 * export routes around it via `exportValue` to keep spreadsheet cells numeric.
	 */
	formatCell: (value: number) => string;
	/**
	 * Color coding for this metric. When set (alongside `formatValue`), the cell renders a
	 * traffic-light `MetricThresholdBadge` (numeric sort/export are unaffected — they read the raw
	 * field value). `formatValue` turns the raw number into the pill's display text.
	 *
	 * Provide `thresholds` for single-value coloring, or `computeStatus` when the color depends on
	 * more than the cell's own value (e.g. actual vs. target from the same row). `formatValue` is
	 * required for either.
	 */
	thresholds?: MetricThresholds;
	computeStatus?: (row: MultiFacilityGridRow) => MetricStatus;
	formatValue?: (value: number) => string;
};

// Two decimal places for every metric that has decimals at all, per the network-view simplification:
// the grid previously mixed none / one / two / three decimals across columns, which read as inconsistent
// rather than as differing precision. Counts (days, users, actions) are whole numbers and stay bare.
const METRIC_DECIMALS = 2;

const formatDecimal = (value: number): string => value.toFixed(METRIC_DECIMALS);
const formatPercent = (value: number): string => `${value.toFixed(METRIC_DECIMALS)}%`;
const formatDollars = (value: number): string => `$${value.toFixed(METRIC_DECIMALS)}`;
const formatCount = (value: number): string => String(Math.round(value));
// The two diff-from-target metrics are signed: the sign IS the reading ("above target" vs "below"), so
// an unsigned 0.37 next to a target column is ambiguous. Matches the catalog's `signed=True` on both.
const withSign = (value: number, format: (absolute: number) => string): string => `${value < 0 ? "-" : "+"}${format(Math.abs(value))}`;
const formatSignedDecimal = (value: number): string => withSign(value, formatDecimal);
const formatSignedDollars = (value: number): string => withSign(value, formatDollars);
// Export keeps the raw number (rounded to the displayed precision) so XLSX cells stay numeric and
// sortable in a spreadsheet, instead of becoming "$12.34" text.
const roundForExport = (value: number): number => Number(value.toFixed(METRIC_DECIMALS));

/**
 * The simplified network metric set (product decision, Aug 2026; reshaped Sep 2026). Order here is the
 * on-screen order and is band-contiguous: every column of a header band (HPPD, PPD, Overtime, Agency,
 * Staff Adoption, Admin Activity, Request Outcomes) sits together, off-by-default ones inside their own
 * band, so a band always renders as ONE merged header cell and a revealed optional column lands beside
 * its siblings rather than at the far end of the table. `optional: true` columns start hidden but stay
 * reachable through the column picker.
 */
const METRIC_COLUMNS: MetricColumnSpec[] = [
	// ------------------------------------------------------------------------------------------ HPPD
	{
		field: "targetHppd",
		headerName: "ops.backoffice.target.hppd",
		tooltip: "mc.multi.grid.tooltip.target.hppd",
		minWidth: METRIC_COLUMN_MIN_WIDTH,
		formatCell: formatDecimal,
	},
	{
		field: "avgActualHppd",
		headerName: "ops.backoffice.avg.actual.hppd",
		tooltip: "mc.multi.grid.tooltip.avg.actual.hppd",
		minWidth: METRIC_COLUMN_MIN_WIDTH,
		formatCell: formatDecimal,
		computeStatus: (row) => resolveStatusAgainstReference(row.avgActualHppd, row.targetHppd, OVER_REFERENCE_THRESHOLDS),
		formatValue: formatDecimal,
	},
	{
		field: "avgScheduledHppd",
		headerName: "mc.multi.grid.avg.scheduled.hppd",
		tooltip: "mc.multi.grid.tooltip.avg.scheduled.hppd",
		minWidth: WIDE_METRIC_COLUMN_MIN_WIDTH,
		formatCell: formatDecimal,
	},
	{
		field: "hppdDiffFromTarget",
		headerName: "ops.backoffice.hppd.diff.target",
		tooltip: "mc.multi.grid.tooltip.hppd.diff.target",
		minWidth: METRIC_COLUMN_MIN_WIDTH,
		formatCell: formatSignedDecimal,
	},
	// Reached only through the "HPPD by Role" / "HPPD Agency vs Non-Agency" group rows.
	{
		field: "avgActualHppdCna",
		headerName: "mc.multi.grid.hppd.cna",
		tooltip: "mc.multi.grid.tooltip.hppd.cna",
		minWidth: METRIC_COLUMN_MIN_WIDTH,
		optional: true,
		excludeFromColumnPicker: true,
		formatCell: formatDecimal,
	},
	{
		field: "avgActualHppdLpn",
		headerName: "mc.multi.grid.hppd.lpn",
		tooltip: "mc.multi.grid.tooltip.hppd.lpn",
		minWidth: METRIC_COLUMN_MIN_WIDTH,
		optional: true,
		excludeFromColumnPicker: true,
		formatCell: formatDecimal,
	},
	{
		field: "avgActualHppdRn",
		headerName: "mc.multi.grid.hppd.rn",
		tooltip: "mc.multi.grid.tooltip.hppd.rn",
		minWidth: METRIC_COLUMN_MIN_WIDTH,
		optional: true,
		excludeFromColumnPicker: true,
		formatCell: formatDecimal,
	},
	{
		field: "avgActualHppdAgency",
		headerName: "mc.multi.grid.hppd.agency",
		tooltip: "mc.multi.grid.tooltip.hppd.agency",
		minWidth: WIDE_METRIC_COLUMN_MIN_WIDTH,
		optional: true,
		excludeFromColumnPicker: true,
		formatCell: formatDecimal,
	},
	{
		field: "avgActualHppdNonAgency",
		headerName: "mc.multi.grid.hppd.non.agency",
		tooltip: "mc.multi.grid.tooltip.hppd.non.agency",
		minWidth: WIDE_METRIC_COLUMN_MIN_WIDTH,
		optional: true,
		excludeFromColumnPicker: true,
		formatCell: formatDecimal,
	},
	// ------------------------------------------------------------------------------------------- PPD
	{
		field: "targetPpd",
		headerName: "mc.multi.grid.target.ppd",
		tooltip: "mc.multi.grid.tooltip.target.ppd",
		minWidth: METRIC_COLUMN_MIN_WIDTH,
		wageBearing: true,
		formatCell: formatDollars,
	},
	{
		field: "avgActualPpd",
		headerName: "mc.multi.grid.avg.actual.ppd",
		tooltip: "mc.multi.grid.tooltip.avg.actual.ppd",
		minWidth: METRIC_COLUMN_MIN_WIDTH,
		wageBearing: true,
		formatCell: formatDollars,
	},
	{
		field: "avgScheduledPpd",
		headerName: "mc.multi.grid.avg.scheduled.ppd",
		tooltip: "mc.multi.grid.tooltip.avg.scheduled.ppd",
		minWidth: WIDE_METRIC_COLUMN_MIN_WIDTH,
		wageBearing: true,
		formatCell: formatDollars,
	},
	{
		field: "ppdDiffFromTarget",
		headerName: "mc.multi.grid.ppd.diff.target",
		tooltip: "mc.multi.grid.tooltip.ppd.diff.target",
		minWidth: WIDE_METRIC_COLUMN_MIN_WIDTH,
		wageBearing: true,
		formatCell: formatSignedDollars,
	},
	// Reached only through the "PPD by Role" / "PPD Agency vs Non-Agency" group rows.
	{
		field: "avgActualPpdCna",
		headerName: "mc.multi.grid.ppd.cna",
		tooltip: "mc.multi.grid.tooltip.ppd.cna",
		minWidth: METRIC_COLUMN_MIN_WIDTH,
		optional: true,
		excludeFromColumnPicker: true,
		wageBearing: true,
		formatCell: formatDollars,
	},
	{
		field: "avgActualPpdLpn",
		headerName: "mc.multi.grid.ppd.lpn",
		tooltip: "mc.multi.grid.tooltip.ppd.lpn",
		minWidth: WIDE_METRIC_COLUMN_MIN_WIDTH,
		optional: true,
		excludeFromColumnPicker: true,
		wageBearing: true,
		formatCell: formatDollars,
	},
	{
		field: "avgActualPpdRn",
		headerName: "mc.multi.grid.ppd.rn",
		tooltip: "mc.multi.grid.tooltip.ppd.rn",
		minWidth: METRIC_COLUMN_MIN_WIDTH,
		optional: true,
		excludeFromColumnPicker: true,
		wageBearing: true,
		formatCell: formatDollars,
	},
	{
		field: "avgActualPpdAgency",
		headerName: "mc.multi.grid.ppd.agency",
		tooltip: "mc.multi.grid.tooltip.ppd.agency",
		minWidth: WIDE_METRIC_COLUMN_MIN_WIDTH,
		optional: true,
		excludeFromColumnPicker: true,
		wageBearing: true,
		formatCell: formatDollars,
	},
	{
		field: "avgActualPpdNonAgency",
		headerName: "mc.multi.grid.ppd.non.agency",
		tooltip: "mc.multi.grid.tooltip.ppd.non.agency",
		minWidth: WIDE_METRIC_COLUMN_MIN_WIDTH,
		optional: true,
		excludeFromColumnPicker: true,
		wageBearing: true,
		formatCell: formatDollars,
	},
	// -------------------------------------------------------------------------------------- Overtime
	{
		field: "otCostPercent",
		headerName: "ops.backoffice.ot.cost.percent",
		tooltip: "mc.multi.grid.tooltip.ot.cost.percent",
		minWidth: METRIC_COLUMN_MIN_WIDTH,
		wageBearing: true,
		formatCell: formatPercent,
	},
	{
		field: "otHoursPercent",
		headerName: "ops.backoffice.ot.hours.percent",
		tooltip: "mc.multi.grid.tooltip.ot.hours.percent",
		minWidth: METRIC_COLUMN_MIN_WIDTH,
		optional: true,
		formatCell: formatPercent,
	},
	// Reached only through the two "OT Hours Breakdown" group rows.
	{
		field: "actualOtHoursCna",
		headerName: "mc.multi.grid.ot.hours.cna",
		tooltip: "mc.multi.grid.tooltip.ot.hours.cna",
		minWidth: WIDE_METRIC_COLUMN_MIN_WIDTH,
		optional: true,
		excludeFromColumnPicker: true,
		formatCell: formatDecimal,
	},
	{
		field: "actualOtHoursLpn",
		headerName: "mc.multi.grid.ot.hours.lpn",
		tooltip: "mc.multi.grid.tooltip.ot.hours.lpn",
		minWidth: WIDE_METRIC_COLUMN_MIN_WIDTH,
		optional: true,
		excludeFromColumnPicker: true,
		formatCell: formatDecimal,
	},
	{
		field: "actualOtHoursRn",
		headerName: "mc.multi.grid.ot.hours.rn",
		tooltip: "mc.multi.grid.tooltip.ot.hours.rn",
		minWidth: WIDE_METRIC_COLUMN_MIN_WIDTH,
		optional: true,
		excludeFromColumnPicker: true,
		formatCell: formatDecimal,
	},
	{
		field: "otHoursPercentCna",
		headerName: "mc.multi.grid.ot.hours.percent.cna",
		tooltip: "mc.multi.grid.tooltip.ot.hours.percent.cna",
		minWidth: METRIC_COLUMN_MIN_WIDTH,
		optional: true,
		excludeFromColumnPicker: true,
		formatCell: formatPercent,
	},
	{
		field: "otHoursPercentLpn",
		headerName: "mc.multi.grid.ot.hours.percent.lpn",
		tooltip: "mc.multi.grid.tooltip.ot.hours.percent.lpn",
		minWidth: WIDE_METRIC_COLUMN_MIN_WIDTH,
		optional: true,
		excludeFromColumnPicker: true,
		formatCell: formatPercent,
	},
	{
		field: "otHoursPercentRn",
		headerName: "mc.multi.grid.ot.hours.percent.rn",
		tooltip: "mc.multi.grid.tooltip.ot.hours.percent.rn",
		minWidth: METRIC_COLUMN_MIN_WIDTH,
		optional: true,
		excludeFromColumnPicker: true,
		formatCell: formatPercent,
	},
	// ---------------------------------------------------------------------------------------- Agency
	{
		field: "agencyCostPercent",
		headerName: "ops.backoffice.agency.cost.percent",
		tooltip: "mc.multi.grid.tooltip.agency.cost.percent",
		minWidth: WIDE_METRIC_COLUMN_MIN_WIDTH,
		wageBearing: true,
		formatCell: formatPercent,
	},
	{
		field: "actualAgencyHours",
		headerName: "ops.backoffice.total.agency.hours",
		tooltip: "mc.multi.grid.tooltip.total.agency.hours",
		minWidth: WIDE_METRIC_COLUMN_MIN_WIDTH,
		optional: true,
		formatCell: formatDecimal,
	},
	// -------------------------------------------------------------------------------- Staff Adoption
	{
		field: "wauPercent",
		headerName: "mc.multi.grid.staff.active.rate",
		tooltip: "mc.multi.grid.tooltip.staff.active.rate",
		minWidth: WIDE_METRIC_COLUMN_MIN_WIDTH,
		formatCell: formatPercent,
	},
	{
		field: "loggedInPercent",
		headerName: "mc.multi.grid.onboarding.percent",
		tooltip: "mc.multi.grid.tooltip.onboarding.percent",
		minWidth: METRIC_COLUMN_MIN_WIDTH,
		formatCell: formatPercent,
		thresholds: PERCENT_HIGHER_IS_BETTER_THRESHOLDS,
		formatValue: formatPercent,
	},
	{
		field: "wauCount",
		headerName: "ops.backoffice.wau",
		tooltip: "mc.multi.grid.tooltip.wau",
		minWidth: WIDE_METRIC_COLUMN_MIN_WIDTH,
		optional: true,
		formatCell: formatCount,
	},
	// -------------------------------------------------------------------------------- Admin Activity
	{
		field: "schedulerLoginCount",
		headerName: "mc.multi.grid.scheduler.logins",
		tooltip: "mc.multi.grid.tooltip.scheduler.logins",
		minWidth: WIDE_METRIC_COLUMN_MIN_WIDTH,
		formatCell: formatCount,
	},
	{
		field: "schedulerActionsCount",
		headerName: "mc.multi.grid.scheduler.actions",
		tooltip: "mc.multi.grid.tooltip.scheduler.actions",
		minWidth: WIDE_METRIC_COLUMN_MIN_WIDTH,
		formatCell: formatCount,
	},
	{
		field: "adminLoginCount",
		headerName: "mc.multi.grid.admin.login.count",
		tooltip: "mc.multi.grid.tooltip.admin.login.count",
		minWidth: WIDE_METRIC_COLUMN_MIN_WIDTH,
		formatCell: formatCount,
	},
	// ------------------------------------------------------------------------------ Request Outcomes
	// Keeps the "Staff Pick-Up" label clients were taught, but is bound to the open-shift pickup rate:
	// the share of open shifts that got picked up is the question this column answers.
	{
		field: "shiftPickupPercent",
		headerName: "ops.backoffice.staff.pickup.percent",
		tooltip: "mc.multi.grid.tooltip.shift.pickup.percent",
		minWidth: WIDE_METRIC_COLUMN_MIN_WIDTH,
		formatCell: formatPercent,
	},
	{
		field: "filledPercent",
		headerName: "ops.backoffice.filled.percent",
		tooltip: "mc.multi.grid.tooltip.filled.percent",
		minWidth: METRIC_COLUMN_MIN_WIDTH,
		optional: true,
		formatCell: formatPercent,
	},
];

/**
 * The columns a hide-wage portfolio must not see at all. Kept as a derived list rather than a second
 * hand-maintained one, so flagging a spec `wageBearing` is the only edit a new money column needs.
 */
const visibleMetricColumns = (wageAllowed: boolean): MetricColumnSpec[] =>
	wageAllowed ? METRIC_COLUMNS : METRIC_COLUMNS.filter((column) => !column.wageBearing);

/**
 * Synthetic picker rows: one checkbox that shows or hides a whole block of breakdown columns in a single
 * click, in place of listing each column individually. `controlledFields` are the real column fields the
 * row toggles together; each block lives inside one header band, and the row joins that band's picker
 * section.
 */
type PickerGroupSpec = {
	id: string;
	label: TranslationKeys;
	controlledFields: (keyof MultiFacilityGridRow)[];
};

const PICKER_GROUP_SPECS: PickerGroupSpec[] = [
	{ id: "hppdByRole", label: "mc.multi.grid.hppd.by.role", controlledFields: ["avgActualHppdCna", "avgActualHppdLpn", "avgActualHppdRn"] },
	{ id: "hppdAgencySplit", label: "mc.multi.grid.hppd.agency.split", controlledFields: ["avgActualHppdAgency", "avgActualHppdNonAgency"] },
	{ id: "ppdByRole", label: "mc.multi.grid.ppd.by.role", controlledFields: ["avgActualPpdCna", "avgActualPpdLpn", "avgActualPpdRn"] },
	{ id: "ppdAgencySplit", label: "mc.multi.grid.ppd.agency.split", controlledFields: ["avgActualPpdAgency", "avgActualPpdNonAgency"] },
	{ id: "otHoursBreakdown", label: "mc.multi.grid.ot.hours.breakdown", controlledFields: ["actualOtHoursCna", "actualOtHoursLpn", "actualOtHoursRn"] },
	{
		id: "otHoursBreakdownPercent",
		label: "mc.multi.grid.ot.hours.breakdown.percent",
		controlledFields: ["otHoursPercentCna", "otHoursPercentLpn", "otHoursPercentRn"],
	},
];

/**
 * The group rows, under the same wage gate as the columns: a row only controls fields that are actually
 * rendered, and a row left with nothing to control (the PPD split for a hide-wage user) is not offered.
 */
export const useMultiFacilityGridColumnPickerGroups = (wageAllowed: boolean): SyntheticPickerColumn[] => {
	const { t } = useTranslation();
	return useMemo(() => {
		const rendered = new Set<string>(visibleMetricColumns(wageAllowed).map((column) => column.field));
		return PICKER_GROUP_SPECS.flatMap((group) => {
			const controlledFields = group.controlledFields.filter((field) => rendered.has(field));
			if (controlledFields.length === 0) return [];
			return [{ id: group.id, label: t(group.label), controlledFields, columnPickerGroup: metricGroupForField(controlledFields[0]) }];
		});
	}, [t, wageAllowed]);
};

/** The picker's sections, in this order — the same bands (and labels) the header uses. */
const PICKER_SECTIONS: MetricColumnGroupKey[] = ["hppd", "ppd", "overtime", "agency", "staffAdoption", "adminActivity", "requestOutcomes"];

/** Pre-translated `band → label` for `columnPicker.groupLabels`; its key order is the section order. */
export const useMultiFacilityGridColumnPickerGroupLabels = (): Record<string, string> => {
	const { t } = useTranslation();
	return useMemo(() => Object.fromEntries(PICKER_SECTIONS.map((key) => [key, t(METRIC_GROUP_LABEL_KEYS[key])])), [t]);
};

/**
 * A money cell for a facility whose wage data this user may not see. The backend sends 0 rather than
 * null so the column always carries a number (product decision), which makes it indistinguishable
 * from a real $0 — the tooltip is what tells the two apart.
 */
const withheldCell = (formatted: string, label: string): ReactNode => (
	<InHouseTooltip title={label}>
		<Box component="span" sx={{ color: INK.SECONDARY, cursor: "help" }}>
			{formatted}
		</Box>
	</InHouseTooltip>
);

/**
 * Default column visibility: optional columns start hidden (`false`); all others default visible.
 * Seeds the grid's first paint and is what the column picker's "Reset" restores to.
 *
 * `useColumnVisibility` merges a user's stored model OVER this. The picker stores the WHOLE model it
 * held at the user's last toggle — this default's `false`s included — so for anyone who has ever used
 * the picker, only a column ADDED since that save picks up its default here. Changing the default of an
 * existing column (e.g. making a hidden one visible) does not reach those users unless the grid's
 * storage key (`managementCenterMultiGridKey`) is bumped. A bump carries the user's order, sort and
 * widths over once (`migrateLegacyMultiFacilityGridStorage`) but never their visibility.
 */
export const MULTI_FACILITY_GRID_DEFAULT_VISIBILITY: GridColumnVisibilityModel = Object.fromEntries(
	METRIC_COLUMNS.filter((column) => column.optional).map((column) => [column.field, false]),
);

/**
 * The metric columns as `{key, label}`, in on-screen order and under the same wage gate the grid
 * applies — what the emailed report offers, and where its spreadsheet headers come from.
 *
 * The label is resolved here rather than on the backend because these header names are frontend
 * translation keys, and several deliberately read differently from the metric catalog's own
 * ops-facing wording. See `PushReportColumn`.
 */
export const useMultiFacilityGridExportableColumns = (wageAllowed: boolean): PushReportColumn[] => {
	const { t } = useTranslation();
	return useMemo(() => visibleMetricColumns(wageAllowed).map((column) => ({ key: column.field, label: t(column.headerName) })), [wageAllowed, t]);
};

/**
 * Read-only metric columns for the multi-facility grid — the network-level, backoffice-style table
 * head nurses see under the "All Facilities" view. The pinned Facility name column (left-aligned as the
 * row anchor) plus the metric set above. Every metric column is read-only and center-aligns both header
 * and value; `type: "number"` keeps numeric sorting/formatting. `flex` fills the grid's width (facility
 * weighted heavier); `minWidth` is the floor, sized per tier so header labels never truncate even when
 * flex can't grow (many columns shown → grid at min width).
 *
 * `wageAllowed` comes from `usePortfolioWageAllowed()`; false drops every money column from the set.
 */
export const useMultiFacilityGridColumns = (
	wageAllowed: boolean,
	onRemoveFacility: (departmentId: string) => void,
	wageRestrictedDepartmentIds: ReadonlySet<string>,
): SystemGridColDef<MultiFacilityGridRow>[] => {
	const { t } = useTranslation();
	const theme = useTheme();
	// The grid renders its header titles in body2 at the bold weight SystemDataGrid sets.
	const headerFont = `${theme.typography.fontWeightBold} ${theme.typography.body2.fontSize} ${theme.typography.fontFamily}`;

	return useMemo<SystemGridColDef<MultiFacilityGridRow>[]>(
		() => [
			{
				// Drops this facility from the page-level selection, so the comparison can be curated
				// from the table itself rather than only through the Facilities dropdown. Pinned beside
				// the facility name, out of the column picker and the sort catalog: it holds no data.
				field: MULTI_FACILITY_GRID_REMOVE_FIELD,
				headerName: "",
				width: REMOVE_COLUMN_WIDTH,
				minWidth: REMOVE_COLUMN_WIDTH,
				// Fixed: the frozen Facility column sticks at exactly this offset.
				resizable: false,
				sortable: false,
				disableExport: true,
				excludeFromColumnPicker: true,
				disableReorder: true,
				align: "center",
				renderCell: ({ row }) => (
					<Tooltip title={t("mc.multi.grid.remove.facility", { facility: row.departmentName })}>
						<IconButton
							size="small"
							onClick={() => onRemoveFacility(row.departmentId)}
							aria-label={t("mc.multi.grid.remove.facility", { facility: row.departmentName })}
							data-testid={`multi-facility-grid-remove-${row.departmentId}`}
						>
							<CloseIcon sx={{ fontSize: 16 }} />
						</IconButton>
					</Tooltip>
				),
			},
			{
				field: "departmentName",
				headerName: t("mc.multi.grid.facility"),
				flex: FACILITY_COLUMN_FLEX,
				minWidth: FACILITY_COLUMN_MIN_WIDTH,
				headerAlign: "left",
				align: "left",
				// Pinned identity column — always visible (out of the column picker) and pinned first (not
				// draggable) so it stays the row's anchor while the metric columns reorder around it.
				excludeFromColumnPicker: true,
				disableReorder: true,
			},
			...visibleMetricColumns(wageAllowed).map((column): SystemGridColDef<MultiFacilityGridRow> => {
				const { thresholds, computeStatus, formatValue, formatCell } = column;
				const headerName = t(column.headerName);
				return {
					field: column.field,
					headerName,
					type: "number",
					flex: METRIC_COLUMN_FLEX,
					minWidth: Math.max(column.minWidth, Math.ceil(measureTextWidth(headerName, headerFont)) + METRIC_HEADER_CHROME_WIDTH),
					headerAlign: "center",
					align: "center",
					// Breakdown columns are toggled by their group row, never listed alone.
					...(column.excludeFromColumnPicker ? { excludeFromColumnPicker: true } : {}),
					// Two-tier header band (shared taxonomy with the ops grid), which doubles as the picker
					// section so the picker reads in the same blocks as the header. The pinned Facility +
					// remove columns are ungrouped and sit under a blank band cell.
					headerGroup: metricGroupForField(column.field),
					columnPickerGroup: metricGroupForField(column.field),
					// The metric's definition, on header hover. `description` is the accessible/native
					// fallback; `headerTooltip` is what actually renders, since SortableDataGridHeader's
					// custom renderer suppresses MUI's native `description` tooltip.
					description: t(column.tooltip),
					headerTooltip: metricHeaderTooltip(headerName, t(column.tooltip)),
					// Rounds the plain (non-badge) cell text; badge columns override the on-screen cell via
					// renderCell. Export bypasses both (see getExportValue) to stay numeric.
					valueFormatter: (value) => (typeof value === "number" ? formatCell(value) : ""),
					getExportValue: (row) => {
						const value = row[column.field];
						return typeof value === "number" ? roundForExport(value) : null;
					},
					// A money column on a wage-restricted facility renders its zero as visibly withheld.
					...(column.wageBearing
						? {
								renderCell: ({ value, row }) => {
									const formatted = typeof value === "number" ? formatCell(value) : "";
									return wageRestrictedDepartmentIds.has(row.departmentId) ? withheldCell(formatted, t("mc.multi.grid.wage.withheld")) : formatted;
								},
							}
						: {}),
					...(formatValue && (thresholds || computeStatus)
						? {
								renderCell: ({ value, row }) => (
									<MetricThresholdBadge
										value={typeof value === "number" ? value : null}
										formatValue={formatValue}
										thresholds={thresholds}
										status={computeStatus ? computeStatus(row) : undefined}
										dataTestId={`multi-facility-grid-${toKebabCase(column.field)}-badge-${row.departmentId}`}
									/>
								),
							}
						: {}),
				};
			}),
		],
		[t, headerFont, wageAllowed, onRemoveFacility, wageRestrictedDepartmentIds],
	);
};

/** The hover card shown on a metric header: the column's name over its plain-language definition. */
const metricHeaderTooltip = (headerName: string, definition: string): ReactNode => (
	<Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, py: 0.5, maxWidth: 280 }}>
		<Box sx={{ fontWeight: 700, fontSize: 12 }}>{headerName}</Box>
		<Box sx={{ fontSize: 11.5, lineHeight: 1.4 }}>{definition}</Box>
	</Box>
);

/**
 * Sort catalog for the multi-facility grid — one axis per column (Facility name plus every metric).
 * Passing this to `SystemDataGrid` turns each column header into a clickable sort trigger. Metric
 * values are nullable (a facility may have no data for a range, and a hide-wage portfolio blanks the
 * money ones), so nullish rows pin to the end in both directions.
 */
export const useMultiFacilityGridSortOptions = (wageAllowed: boolean): MultiSortOption<MultiFacilityGridRow>[] => {
	const { t } = useTranslation();

	return useMemo<MultiSortOption<MultiFacilityGridRow>[]>(
		() => [
			{ label: t("mc.multi.grid.facility"), sortColumns: ["departmentName"] },
			...visibleMetricColumns(wageAllowed).map((column) => ({
				label: t(column.headerName),
				sortColumns: [column.field],
				sortNullUndefinedLast: true,
			})),
		],
		[t, wageAllowed],
	);
};
